import React, { useEffect, useContext } from "react";
import Layout from "@wrappers/layouts/main-layout/Layout";
import { mainContext } from "@ts/constants";
import TabsComponent from "../../components/tabs/TabsComponent";
import { useClearGridData, useGetModulesData } from "@hooks/AllCustomHooks";
import { useSelector, useDispatch } from "react-redux";
import { setIsLoader } from "@features/loaderSlice";
import { setDashboardLoader } from "@features/dashboardLoaderSlice";
import { updateTitle } from "@features/titleSlice";
import { updateActiveModule } from "@features/activeModuleSlice";
import Overview from "./overview/Overview";
import {
    agBtnExport,
    setPillBadges,
    resetPillBadges,
    getActiveModule,
    getClientTagsList,
    getGoalDataValues,
    populateGoals,
    getGoalsConfig,
    titleCase,
} from "@ts/utils";
import {
    populatePortfolios,
    populateRulesOverview,
    populateCampaigns,
    populateKeywords,
    populatePlacements,
    populateAdGroups,
    populateProductAds,
    populateAlerts,
    populateTagTypeAggregatedView,
} from "@ts/retrieve-data";
import {
    setPortfoliosData,
    setCampaignsData,
    setAdgroupsData,
    setKeywordsData,
    setPlacementsData,
    setProductsData,
    updateRecordTagsData,
    setGoalData,
    setTagTypeAggregatedData,
    setTagTypeOptions,
    setSelectedTagTypeOption,
    setCampaignLevelTotalSpend,
    setTagTypeLevelTotalSpend,
} from "@features/gridDataSlice";
import * as Sentry from "@sentry/react";
import { loadCharts } from "@ts/charts";
import {
    AdGroup,
    Alert,
    Campaign,
    TClientTags,
    IActiveTab,
    IChannel,
    IMainContext,
    Keyword,
    Portfolio,
    ProductAd,
    Rule,
    TActiveClientId,
    TGridApi,
    TGoalDataValues,
    IValueBoolean,
    SubModule,
    ParentModule,
    TGoalsConfig,
} from "@ts/common-types";
import { updateClientTagsList } from "@/features/clientDataSlice";
import { flatten, filter, pluck, uniq, map } from "underscore";
import { setGoalDataValues } from "@/features/modalSlice";
import { setGoalsConfig } from "@/features/adminSettingsSlice";

const PerformanceOverview = React.memo(() => {
    const {
        dates,
        setAlertsData,
        setActiveRulesData,
        DateRange,
        setAdgroupLoading,
        setCampaignsLoading,
        setKeywordsLoading,
        setPlacementsLoading,
        setPortfoliosLoading,
        setProductsLoading,
        setAlertsLoading,
        setActiveRulesLoading,
        setGoalsGridLoading,
        setTagTypeAggregatedGridLoading,
        goalsGridApi,
    } = useContext<IMainContext>(mainContext);

    const showAddGoalsModal = useSelector(
        (state: { addGoalsModalDisplay: IValueBoolean }) =>
            state.addGoalsModalDisplay.value
    );

    const isGoalFormSubmitted = useSelector(
        (state: { isGoalFormSubmitted: IValueBoolean }) =>
            state.isGoalFormSubmitted.value
    );

    const globalGridApi = useSelector(
        (state: TGridApi) => state.globalGridApi.value
    );
    const activeTab = useSelector((state: IActiveTab) => state.activeTab.value);
    const { activeModule } = getActiveModule();
    const channel = useSelector((state: IChannel) => state.channel.value);
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );
    const routesData = useGetModulesData({ type: "routes" });
    const loaderCount = useGetModulesData({ type: "loader-count" });
    const routeView = (route: SubModule) => routesData.includes(route);

    let loader = 0;
    const dispatch = useDispatch();
    const clearGridData = useClearGridData();

    // TODO: Refactor this, when we integrate Redux rtx or React query
    const incrementLoader = () => {
        loader = loader + 1;
        if (loader === loaderCount) {
            dispatch(setIsLoader(false));
        }
    };

    const populateFunnel = async () => {
        sessionStorage.removeItem("selectedCards");
        dispatch(setDashboardLoader(true));
        await loadCharts();
    };

    useEffect(() => {
        getClientTagsList()
            .then((tagsList: TClientTags[]) => {
                dispatch(
                    updateClientTagsList(
                        filter(tagsList, (tag) =>
                            ["global", "campaign"].includes(tag.record_type)
                        )
                    )
                );
            })
            .catch((error) => {
                console.error(error);
            });
    }, [activeClientId, channel]);

    useEffect(() => {
        if (
            activeModule === ParentModule.PERFORMANCE_OVERVIEW ||
            activeModule === SubModule.OVERVIEW
        ) {
            populateFunnel();
        }
        // eslint-disable-next-line
    }, [activeClientId, dates, DateRange, dispatch, activeModule, channel]);

    useEffect(() => {
        populateRulesOverview()
            .then((rules) => {
                if (rules) {
                    const activeRules = rules.filter(
                        (item: Rule) => item.status === "active"
                    ) as Array<Rule>;
                    setActiveRulesData(activeRules);
                }
            })
            .catch((error) => {
                console.error(error);
                Sentry.captureException(error);
            })
            .finally(() => {
                setActiveRulesLoading(false);
                // incrementLoader();
            });
        // eslint-disable-next-line
    }, [activeClientId, dates, DateRange, dispatch, channel]);

    useEffect(() => {
        populateAlerts()
            .then((alerts: Alert[]) => {
                if (alerts) {
                    setAlertsData(alerts);
                }
            })
            .catch((error) => {
                console.error(error);
                Sentry.captureException(error);
            })
            .finally(() => {
                setAlertsLoading(false);
                incrementLoader();
            });
        // eslint-disable-next-line
    }, [activeClientId, dates, DateRange, dispatch, channel]);

    useEffect(() => {
        populateTagTypeAggregatedView()
            .then((tagTypeAggregatedData) => {
                if (tagTypeAggregatedData) {
                    const tagTypeOptions = uniq(
                        map(tagTypeAggregatedData, (data) => data.tag_type)
                    );
                    dispatch(setTagTypeOptions(tagTypeOptions));
                    dispatch(setTagTypeAggregatedData(tagTypeAggregatedData));
                    dispatch(setSelectedTagTypeOption(tagTypeOptions[0]));
                }
            })
            .catch((error) => {
                console.error(error);
                Sentry.captureException(error);
            })
            .finally(() => {
                setTagTypeAggregatedGridLoading(false);
            });
        // eslint-disable-next-line
    }, [activeClientId, dates, DateRange, dispatch, channel]);

    useEffect(() => {
        getGoalsConfig()
            .then((config: TGoalsConfig[]) => {
                if (config) {
                    dispatch(setGoalsConfig(config));
                }
            })
            .catch((error) => {
                console.error(error);
                Sentry.captureException(error);
            });
    }, [activeClientId, dates, DateRange, dispatch, channel]);

    useEffect(() => {
        getGoalDataValues()
            .then((dataValues: TGoalDataValues) => {
                if (dataValues) {
                    dispatch(setGoalDataValues(dataValues));
                }
            })
            .catch((error) => {
                console.error(error);
                Sentry.captureException(error);
            });
    }, [activeClientId, dates, DateRange, dispatch, channel]);

    useEffect(() => {
        resetPerformanceOverview();

        dispatch(updateTitle(titleCase(ParentModule.PERFORMANCE_OVERVIEW)));
        dispatch(updateActiveModule(SubModule.OVERVIEW));

        routeView(SubModule.PORTFOLIO) &&
            populatePortfolios()
                .then((portfolios: Portfolio[]) => {
                    if (portfolios) {
                        setPillBadges("portfolios-badge", portfolios.length);
                        dispatch(setPortfoliosData(portfolios));
                    }
                })
                .catch((error) => {
                    console.error(error);
                    Sentry.captureException(error);
                })
                .finally(() => {
                    setPortfoliosLoading(false);
                    incrementLoader();
                });

        routeView(SubModule.CAMPAIGN) &&
            populateCampaigns()
                .then((campaigns: Campaign[]) => {
                    setPillBadges("campaigns-badge", campaigns.length);
                    dispatch(setCampaignsData(campaigns));
                    dispatch(
                        updateRecordTagsData(
                            uniq(flatten(pluck(campaigns, "campaign_tags")))
                        )
                    );
                })
                .catch((error) => {
                    console.error(error);
                    Sentry.captureException(error);
                })
                .finally(() => {
                    setCampaignsLoading(false);
                    incrementLoader();
                });

        routeView(SubModule.KEYWORD) &&
            populateKeywords()
                .then((keywords: Keyword[]) => {
                    if (keywords) {
                        setPillBadges("keywords-badge", keywords.length);
                        dispatch(setKeywordsData(keywords));
                    }
                })
                .catch((error) => {
                    console.error(error);
                    Sentry.captureException(error);
                })
                .finally(() => {
                    setKeywordsLoading(false);
                    incrementLoader();
                });

        routeView(SubModule.PLACEMENTS) &&
            populatePlacements()
                .then((placements) => {
                    setPillBadges(
                        "placements-badge",
                        (placements as Array<Keyword>).length
                    );
                    dispatch(setPlacementsData(placements as Array<Keyword>));
                    setPlacementsLoading(false);
                    incrementLoader();
                })
                .catch((error) => {
                    console.error(error);
                    Sentry.captureException(error);
                });

        routeView(SubModule.ADGROUP) &&
            populateAdGroups()
                .then((adgroups: AdGroup[]) => {
                    if (adgroups) {
                        setPillBadges("adgroups-badge", adgroups.length);
                        dispatch(setAdgroupsData(adgroups));
                    }
                })
                .catch((error) => {
                    console.error(error);
                    Sentry.captureException(error);
                })
                .finally(() => {
                    setAdgroupLoading(false);
                    incrementLoader();
                });

        routeView(SubModule.PRODUCT) &&
            populateProductAds()
                .then((productAds: ProductAd[]) => {
                    if (productAds) {
                        setPillBadges("products-badge", productAds.length);
                        dispatch(setProductsData(productAds));
                    }
                })
                .catch((error) => {
                    console.error(error);
                    Sentry.captureException(error);
                })
                .finally(() => {
                    setProductsLoading(false);
                    incrementLoader();
                });
        // eslint-disable-next-line
    }, [activeClientId, dates, DateRange, dispatch, channel]);

    //to refresh goal data after every add, edit or clone action
    useEffect(() => {
        if (!showAddGoalsModal && isGoalFormSubmitted) {
            populateGoals()
                .then((goals) => {
                    if (goals) {
                        dispatch(setGoalData(goals));
                    }
                    goalsGridApi?.refreshCells({ force: true });
                })
                .catch((error) => {
                    console.error(error);
                    Sentry.captureException(error);
                })
                .finally(() => {
                    setGoalsGridLoading(false);
                });
        }
        // eslint-disable-next-line
    }, [
        activeClientId,
        dates,
        DateRange,
        dispatch,
        channel,
        showAddGoalsModal,
        isGoalFormSubmitted,
    ]);

    const onBtnExport = () => {
        agBtnExport(globalGridApi);
    };

    const resetPerformanceOverview = () => {
        loader = 0;
        resetPillBadges();
        dispatch(setIsLoader(true));
        clearGridData();
        setAdgroupLoading(true);
        setCampaignsLoading(true);
        setKeywordsLoading(true);
        setPlacementsLoading(true);
        setPortfoliosLoading(true);
        setProductsLoading(true);
        setAlertsLoading(true);
        setGoalsGridLoading(true);
        setActiveRulesLoading(true);
        setTagTypeAggregatedGridLoading(true);
        //TODO: Refactor this part
        dispatch(setPortfoliosData([]));
        dispatch(setCampaignsData([]));
        dispatch(setAdgroupsData([]));
        dispatch(setKeywordsData([]));
        dispatch(setPlacementsData([]));
        setActiveRulesData([]);
        setAlertsData([]);
        dispatch(setGoalData([]));
        dispatch(setTagTypeAggregatedData([]));
        dispatch(setTagTypeLevelTotalSpend(0));
        dispatch(setCampaignLevelTotalSpend(0));
    };

    return (
        <>
            <Layout>
                <div className="main-content-body subheader-fixed">
                    <div className="tabbable-line">
                        <TabsComponent onBtnExport={onBtnExport} />
                        {activeTab === "overview" ? <Overview /> : null}
                    </div>
                </div>
            </Layout>
        </>
    );
});

export default PerformanceOverview;
